import React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ROOT,
  FORECAST_REPORT,
  BEST_SELLERS,
  OVERSTOCK,
  HELP,
  PROFILE,
  INCOMING_INVENTORY,
  ONBOARDING,
  AUTH,
  ERROR,
  CONNECTING,
} from "../../navigation/CONSTANTS";
import { ONBOARDING_COMPLETED } from "../../utils/onboardingStepsUtils";
import { isInAppWindow } from "../../utils/appWindowUtils";

/*
 * AppBridgeNavMenu
 *
 * Renders the app's admin-chrome navigation using Shopify's unified App
 * Bridge <s-app-nav>/<s-link> web components (loaded globally via the
 * app-bridge.js script tag in public/index.html). Shopify renders this
 * markup into the admin's own sidebar, outside of our iframe.
 *
 * This component stays mounted at the app root. Unmounting it from PageLayout
 * does not reliably clear Shopify's already-projected sidebar, so we always
 * render <s-app-nav> and swap its children instead.
 *
 * Links are shown only after wizard onboarding is complete. While status is
 * unknown, incomplete, or the merchant is still on /ui/onboarding/..., we
 * render an empty <s-app-nav> so Shopify replaces the sidebar with no items.
 *
 * App Window is a second iframe. Keep <s-app-nav> empty there so the overlay
 * does not re-project Forecast Report / Best Sellers / … into Admin.
 *
 * Only a single flat level of links is supported (no sub-menus). The
 * `rel="home"` link below is hidden from the rendered list and instead
 * makes the app's name/icon in the admin nav point at ROOT.
 *
 * Forecast Report / Best Sellers / Overstock must use distinct pathnames
 * (not query params). App Bridge matches the active nav item by pathname;
 * shared paths make the wrong link stay highlighted.
 *
 * A page stays on its nav item only when its pathname is that href or a
 * child path (`/ui/incominginventory/add` keeps Incoming Inventory active).
 * A sibling like `/ui/incominginventoryadd` does not.
 */
const AppBridgeNavMenu = () => {
  const location = useLocation();
  const { onboardingStepsStatus } = useSelector((state) => state.auth);
  const isOnboardingPath = location.pathname.startsWith(ONBOARDING);
  // Connecting / Auth / Error never used PageLayout, so they must not pick up
  // sidebar links now that this menu lives at the app root.
  const isChromeOnlyPath =
    location.pathname.startsWith(AUTH) ||
    location.pathname.startsWith(ERROR) ||
    location.pathname.startsWith(CONNECTING);
  const showNav =
    onboardingStepsStatus === ONBOARDING_COMPLETED &&
    !isOnboardingPath &&
    !isChromeOnlyPath &&
    !isInAppWindow();

  if (!showNav) {
    return <s-app-nav></s-app-nav>;
  }

  return (
    <s-app-nav>
      <s-link href={ROOT} rel="home"></s-link>
      <s-link href={FORECAST_REPORT}>Forecast Report</s-link>
      <s-link href={BEST_SELLERS}>Best Sellers</s-link>
      <s-link href={OVERSTOCK}>Overstock</s-link>
      <s-link href={INCOMING_INVENTORY}>Incoming Inventory</s-link>
      <s-link href={PROFILE}>Settings</s-link>
      <s-link href={HELP}>Help</s-link>
    </s-app-nav>
  );
};

export default AppBridgeNavMenu;
